import { format, parseISO } from 'date-fns';
import { enUS, ru } from 'date-fns/locale';
import { weekdayToken } from '../../utils/weekday';

/**
 * The ONE date formatter of the six client-page tool sections (design PORTAL-SECTIONS-2026-10-22 §1.5). Every tab used to roll its own
 * `day` / `when` / `dateLabel`; a stand-alone date is "21 Oct 2026" everywhere now.
 *
 * A string that is a calendar day (`2026-10-21`) is parsed as a LOCAL day (`dateOfDay`), never `new Date(iso)` (that is UTC midnight and
 * shows the day before west of Greenwich). Any other string is an ISO instant (`parseISO`).
 */
export type ToolLocale = 'en' | 'ru';

export const dateFnsLocaleOf = (locale: ToolLocale) =>
  locale === 'ru' ? ru : enUS;

const DAY_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/** `YYYY-MM-DD` of a Date in the browser's zone — the day strings the commands take. */
export const dayOf = (d: Date): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** `YYYY-MM-DD` → the local midnight of that day. */
export const dateOfDay = (day: string): Date => {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const asDate = (d: string | Date): Date =>
  d instanceof Date ? d : DAY_ONLY.test(d) ? dateOfDay(d) : parseISO(d);

const fmt = (d: string | Date, locale: ToolLocale, pattern: string) =>
  format(asDate(d), pattern, { locale: dateFnsLocaleOf(locale) });

/** "21 Oct 2026" — every stand-alone date. */
export const fmtDay = (d: string | Date, locale: ToolLocale) =>
  fmt(d, locale, 'd MMM yyyy');

/** "21 Oct" — inside a dated week / period only. */
export const fmtDayShort = (d: string | Date, locale: ToolLocale) =>
  fmt(d, locale, 'd MMM');

/** "Tue 20 Oct" / «вт 20 окт.» — a day row. */
export const fmtWeekday = (d: string | Date, locale: ToolLocale) =>
  fmt(d, locale, `${weekdayToken(locale)} d MMM`);

/** "21 Oct 2026, 18:00" — the Homework list's. */
export const fmtDateTime = (d: string | Date, locale: ToolLocale) =>
  fmt(d, locale, 'd MMM yyyy, HH:mm');

/** "21 Oct, 18:00" — rows inside one dated week. */
export const fmtDayTimeShort = (d: string | Date, locale: ToolLocale) =>
  fmt(d, locale, 'd MMM, HH:mm');

/** What AssignHomeworkDrawer + RepeatEditor give their pickers. */
export const PICKER_DATE = 'yyyy-MM-dd';
export const PICKER_DATE_TIME = 'yyyy-MM-dd HH:mm';
